'use client';
import React, { useContext, useState, forwardRef, useEffect } from 'react';
import languageContext from '@/context/language';
import { useTranslation } from '@/app/i18n/client';
import { GlobalLoading, Table, Button } from '@/shared/components';
import { TableColumn } from '@/shared/components/Table/use-column';
import { useQueryDocSearchResult } from '@/hooks/search-result.hook';
import { useRouter } from 'next/navigation';
import Cookies from 'js-cookie';
import { useEventTracking } from '@/shared/tracking/use-tracking';
import { Trans } from 'react-i18next';
import { scrollToTop } from '@/app/utils';

interface IProps {
  columns: TableColumn<any>[];
  search: string;
  documentType: string;
  resetPage?: boolean;
  setResetPage?: (val: boolean) => void;
}
const ResultsTable = forwardRef<HTMLInputElement, IProps>(function ResultsTable(props, ref) {
  const { lang } = useContext(languageContext);
  const { t } = useTranslation(lang, 'document-search');
  const router = useRouter();
  const { columns, search, documentType, resetPage, setResetPage } = props;
  const [pageSize, setPageSize] = useState<number>(15);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const { trackEvent, trackEventEffect } = useEventTracking();

  const handleClick = (type) => {
    GlobalLoading.show();
    const typeVal = type === 'coa' ? 'coa' : 'msds';
    const buttonTxt = type === 'coa' ? 'req.coa' : 'req.sds';
    Cookies.set('request_type', typeVal);
    const searchObj = {
      searchType: 'document',
      keyword: search,
      continueAction: t(buttonTxt),
    };
    trackEvent({
      key: 'search',
      action: 'search:continue',
      payload: searchObj,
    });
    router.push('/request-form');
  };

  const noDataDesc =
    documentType === 'COA' ? (
      <p className="flex justify-center items-center">
        <p>{t('no.coa.data')}</p>&nbsp;
        <Button
          onClick={() => handleClick('coa')}
          type="link"
          className="p-0 text-left flex items-center h-auto leading-none"
        >
          {t('req.coa')}
        </Button>
      </p>
    ) : (
      <p className="flex justify-center items-center">
        <Trans
          t={t}
          i18nKey="req.sds"
          components={[
            <Button
              key="button"
              onClick={() => handleClick('sds')}
              type="link"
              className="p-0 text-left flex items-center h-auto leading-non ml-1"
            ></Button>,
          ]}
        />
      </p>
    );

  const updatePageNumber = (e) => {
    setPageNumber(e);
  };

  const updatePageSize = (e) => {
    setPageSize(e);
    setPageNumber(1);
  };

  const {
    data: docSearchData,
    isLoading,
    isPreviousData,
    isFetching,
  } = useQueryDocSearchResult({
    pageSize: pageSize,
    page: pageNumber,
    documentType: documentType,
    catalogNumber: search,
    resetPage,
  });

  trackEventEffect(
    {
      key: 'search',
      action:
        docSearchData?.code === '200' && docSearchData?.page?.count !== 0 ? 'search:successful' : 'search:failure',
      payload: {
        searchType: documentType,
        keyword: search,
        totalResults: docSearchData?.page?.count || 0,
      },
    },
    !isFetching && search && docSearchData ? true : false
  );
  useEffect(() => {
    if (resetPage) {
      setPageNumber(1);
      setResetPage(false);
    }
  }, [resetPage]);
  useEffect(() => {
    if (isPreviousData || (isLoading && search !== undefined)) {
      GlobalLoading.show();
    } else {
      GlobalLoading.hide();
    }
  }, [isPreviousData, isLoading, search]);

  useEffect(() => {
    scrollToTop();
  }, [pageSize, pageNumber]);

  return (
    <>
      {docSearchData && (
        <Table
          columns={columns}
          dataSource={docSearchData.data ? docSearchData.data : []}
          pageData={docSearchData.page}
          updatePageNumber={updatePageNumber}
          updatePageSize={updatePageSize}
          noDataDes={noDataDesc}
          pageOptions={[15, 30, 60]}
          className="w-full min-w-[1300px] document-search-table"
        ></Table>
      )}
    </>
  );
});
export { ResultsTable };
